import _ from 'lodash';
import piction from '../utils/piction';
import appConfig from '../appConfig';
import { launchWindow, downloadFile } from './uiUtils';
import { addToWorkspace, moveBetweenWorkspaces, moveUMO } from '../actions/umo';
import { deselectAll } from '../actions/umoGallery';
import { addIntlError, addIntlInfo } from '../actions/messageDialog';
import { SESSION_EXPIRED_MESSAGE } from '../constants/sessionStateTypes';
import { sessionExpired } from '../actions/session';
import { List } from 'immutable';

/**
 * case reducer for umo selections. used by workspacePreview and umoGallery
 * @param state
 * @param action
 * @returns {*}
 */
export const umoSelectCaseReducer = (state, action) => {
  let newState = state;

  action.umoIDList.forEach((umoID) => {
    const foundIndex = newState.get('selectedUMOIDs').indexOf(umoID);

    if (action.isSelect === undefined) {
      if (foundIndex > -1) {
        // exists in the list already so toggle off
        newState = newState.deleteIn(['selectedUMOIDs', foundIndex]);
      } else {
        // not in list, so toggle on
        newState = newState.set(
          'selectedUMOIDs',
          newState.get('selectedUMOIDs').push(umoID)
        );
      }
    } else if (action.isSelect && foundIndex < 0) {
      newState = newState.set(
        'selectedUMOIDs',
        newState.get('selectedUMOIDs').push(umoID)
      );
    } else if (!action.isSelect) {
      if (foundIndex > -1) {
        newState = newState.deleteIn(['selectedUMOIDs', foundIndex]);
      }
    }
  });

  return newState;
};

export const umoSetSelectedUMOIDsReducer = (state, action) => {
  let newState = state;
  let newSelectedUMOs = new List([
    ...new Set(state.get('selectedUMOIDs').concat(action.umoIDList)),
  ]);
  newState = newState.set('selectedUMOIDs', newSelectedUMOs);
  return newState;
};

export const setLastSelectedUMOIDReducer = (state, action) => {
  let newState = state;
  newState = newState.set('lastSelectedUMOID', action.umoID);
  return newState;
};

const getUMOParams = (umoList, searchParams) => {
  const params = [];
  // if a umoList exists in addition to the search params
  // it means a "select all results" was done, and a few umos were "unselected"
  // so those items needs to be excluded
  if (umoList && searchParams) {
    params.push({
      SEARCH: _.isString(searchParams)
        ? searchParams
        : searchParams.toSearchQuery(),
    });
    params.push({ EXCLUDE_UMO_ID: umoList });
  } else if (umoList) {
    params.push({ UMO_ID: umoList });
  } else {
    params.push({
      SEARCH: _.isString(searchParams)
        ? searchParams
        : searchParams.toSearchQuery(),
    });
  }
  return params;
};

const createExportURL = (urlPrefix, params) => {
  const url = `${urlPrefix}?${_.map(params, (value) =>
    _.isArray(Object.values(value)[0])
      ? Object.values(value)[0]
          .map((valID) => `${Object.keys(value)[0]}=${valID}`)
          .join('&')
      : `${Object.keys(value)[0]}=${Object.values(value)[0]}`
  ).join('&')}&CSRF=${piction.session.token}&SURL=${
    piction.session.surl || ''
  }`;
  console.log(url);
  return url;
};

/**
 * Creates the url for excel export
 */
export const createExcelExportURL = (umoList, isAllTags, searchParams) => {
  const params = [
    { n: 'xls_image' },
    { SORT_ON: 'MAP' },
    ...getUMOParams(umoList, searchParams),
  ];

  if (!isAllTags && appConfig.excelExportMetatags) {
    params.push({ META_TAG: appConfig.excelExportMetatags });
  }

  return createExportURL(piction.xmlURL, params);
};

/**
 * Creates the url for ppt export
 */
export const createPPTExportURL = (umoList, searchParams) => {
  const params = [
    { n: 'ppt_image' },
    { SORT_ON: 'MAP' },
    ...getUMOParams(umoList, searchParams),
  ];

  if (appConfig.pptExportMetatags) {
    params.push({ METATAGS: appConfig.pptExportMetatags.join(',') });
  }

  if (appConfig.imageDerivIndexForPPT) {
    params.push({ IMAGE_TYPE: 'ORIGINAL' }, { DERIV: appConfig.imageDerivIndexForPPT });
  }

  return createExportURL(piction.xmlURL, params);
};

/**
 * Creates the url for ZIP download
 */
export const createZIPExportURL = (
  umoList,
  searchParams,
  options,
  isSelectAll
) => {
  const defaultDerivs = [
    { image_type: 'PHOTO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '2' },
    { image_type: 'VIDEO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '2' },
    { image_type: 'AUDIO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '1' },
    { image_type: 'DOCUMENT' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '1' },
  ];

  const params = [
    { n: 'zip_download' },
    { LBOX_SMART_CHECK: 'TRUE' },
    ...(options || defaultDerivs),
  ];

  if (umoList && isSelectAll) {
    params.push({
      SEARCH: _.isString(searchParams)
        ? searchParams
        : searchParams.toSearchQuery(),
    });
    params.push({ EXCLUDE_UMO_ID: umoList });
  } else if (umoList) {
    params.push({ UMO_ID: umoList });
  } else {
    params.push({
      SEARCH: _.isString(searchParams)
        ? searchParams
        : searchParams.toSearchQuery(),
    });
  }

  return createExportURL(piction.xmlURL, params);
};

/**
 * Creates the url for Batch download
 */
export const createBatchDownloadURL = (
  umoList,
  workspaceID,
  options,
  isSelectAll
) => {
  const defaultDerivs = [
    { image_type: 'PHOTO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '2' },
    { image_type: 'VIDEO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '2' },
    { image_type: 'AUDIO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '1' },
    { image_type: 'DOCUMENT' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '1' },
  ];

  const params = [
    { n: 'MNG_DELIVERY' },
    { WORKSPACE_ID: workspaceID },
    // EMAIL_ADDRESS (optional, will use profile or this if specified)
    ...(options || defaultDerivs),
  ];

  // if (umoList && isSelectAll) {
  //   params.push({ SEARCH:
  //         _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() });
  //   params.push({ EXCLUDE_UMO_ID: umoList });
  // } else if (umoList) {
  //   params.push({ UMO_ID: umoList });
  // } else {
  //   params.push({ SEARCH: _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() });
  // }

  return createExportURL(piction.jsonURL, params);
};

export const createUMOModURL = (action, params) => {
  const pars = [
    { n: 'NWS_IMAGE_PROCESS' },
    { ACTION: action },
    { DOWNLOAD: 'TRUE' },
    { ZIP_DOWNLOAD: 'TRUE' },
    { DOWNLOAD_IMAGE_ID: '1' },
    { DOWNLOAD_IMAGE: 'ORIGINAL' },
    { CREATE_NEW_UMO: 'FALSE' },
    ...params,
  ];
  return createExportURL(piction.jsonURL, pars);
};

/**
 * Creates the url for PDF download
 */
export const createPDFExportURL = (
  umoList,
  workspaceID,
  itemsPP,
  isSelectAll
) => {
  const params = [{ acid: '' }, { ipp: itemsPP }, { download_pdf: 'D' }];

  if (umoList && isSelectAll) {
    params.push({ lbox_id: workspaceID });
    params.push({ EXCLUDE_UMO_ID: umoList });
  } else if (umoList) {
    params.push({ UMO_IDS: umoList.map((umoID) => `${umoID}`).join(',') });
  } else {
    params.push({ lbox_id: workspaceID });
  }
  return createExportURL(`${piction.hostURL}cst.lightbox_report`, params);
};

/**
 * Not in use! has logic fail- only gets first (same) 250 umos. will only be good for testing.
 * retrieves bare data for umo call. used for queries that require umo ids
 * @param searchParams Can be a SearchParams instance or search query string
 */
export const retrieveUMOsMinimal = (searchParams) =>
  piction.getUMOs(
    _.isString(searchParams) ? searchParams : searchParams.toSearchQuery(),
    {
      CHECK_ALTERNATE_REPS: 'FALSE',
      FORCE_REFRESH: 'T',
      INCLUDE_MASTER_METADATA: 'FALSE',
      IS_ADVANCED: 'TRUE',
      MAXROWS: '250',
      METADATA: 'NONE',
      ORIGINAL_URLS: 'FALSE',
      RELATED_OBJECTS: 'FALSE',
      SHOW_MASTER_METADATA: 'FALSE',
      START: '0',
      ENABLE_ORIGINAL_ATTRIBS: 'FALSE',
      WEB_URLS: 'TRUE',
      FULL_URL: 'FALSE',
    }
  );

export const runImmediateDownload = (url, dispatch, fileName) => {
  // dispatch(addIntlInfo('umoModifyDialog.inProgressMessage'));
  // popupForDownloads is used when download links aren't working well,
  // and we want items to open in a new window, instead of the current tab
  if (appConfig.usePopupForDownloads) {
    launchWindow(url, dispatch);
  } else {
    downloadFile(url, fileName);
  }
};

/**
 * returns a force-download url from a umo url by adding force_download=T
 * leaves file name (f=filename) at the end of url- for old browser support
 * @param url - original umo url
 * @param UMOName - alternative name if the url doesn't contain one
 * @param fileName - file name from deriv data (fn attribute)
 * @param dispatch- for session validation check in launchWindow func
 * @param isDynamic- for derivatives that are being created on the fly
 * @param email- if the derivative is dynamic, and of type video/audio-
 * it is being generated and sent to the user by email
 */
export const downloadURL = (
  url,
  UMOName,
  fileName,
  dispatch,
  isDynamic,
  email
) => {
  const splitUrlName = _.split(url, '&f=');
  // if derivative has a fileName attribute- it should be used
  // otherwise name in f= in the url or the actual umo name
  const downloadName = fileName || splitUrlName[1] || UMOName;
  // check that an email exists, and is a string
  // for dynamic downloads- an email would be entered
  // for batch download, the users profile email is automatically used
  const urlEmail =
    isDynamic && email && typeof email === 'string'
      ? `&email_address=${email}`
      : '';
  const downloadUrl = `${splitUrlName[0]}&force_download=T&f=${downloadName}${urlEmail}`;
  console.log(downloadUrl);

  if (isDynamic) {
    dispatch(
      addIntlInfo(
        email && typeof email === 'string'
          ? 'workspaceDownloadDialog.emailDownload'
          : 'umoModifyDialog.inProgressMessage'
      )
    );
  }

  if (isDynamic && email) {
    // email does not need a window to pop up, cause the deriv will be sent to the users email
    $.post(downloadUrl).then((res) => {
      if (
        (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) ||
        (res.RETURN_MESSAGE &&
          res.RETURN_MESSAGE.includes(SESSION_EXPIRED_MESSAGE))
      ) {
        dispatch(sessionExpired(res));
      }

      if (typeof res === 'object' || res.RETURN_MESSAGE !== null) {
        // json return type means an email will be sent with the item or an error has occurred
        if (res.z === 'ERROR' || res.errors) {
          dispatch(addIntlError('general.error'));
          console.log(res);
        } else dispatch(addIntlInfo('workspaceDownloadDialog.emailDownload'));
      } else {
        runImmediateDownload(downloadUrl, dispatch, downloadName);
      }
    });
  } else runImmediateDownload(downloadUrl, dispatch, downloadName);
};

export const savetoDropbox = (umoList, dispatch) => {
  const urls = umoList.map((item) => ({
    url: item.umo.webQuality ? item.umo.webQuality.url : item.umo.thumbnail.url,
    filename: item.umo.name,
  }));

  const options = {
    files: urls,
    success: () => {
      dispatch(addIntlInfo('searchResults.options.actions.dropboxSaveSuccess'));
    },
    // eslint-disable-next-line no-console
    progress: (progress) => {
      console.warn(progress);
    },
    cancel: () => {},
    // eslint-disable-next-line no-console
    error: (err) => {
      dispatch(addIntlError('searchResults.options.actions.dropboxSaveError'));
      console.warn(err);
    },
  };

  // eslint-disable-next-line no-undef
  Dropbox.save(options);
};

export const moveUmoHandler = (
  origin,
  target,
  umoList,
  dispatch,
  searchParams,
  isCopy
) => {
  if (target.isWorkspace) {
    if (origin.isWorkspace) {
      // ws to ws
      dispatch(
        moveBetweenWorkspaces(
          origin.id,
          target.id,
          umoList,
          searchParams,
          isCopy
        )
      );
    } else {
      // cat to ws
      dispatch(addToWorkspace(target.id, umoList, searchParams, { isCopy }));
    }
  } else if (!target.isWorkspace) {
    if (origin.isWorkspace) {
      // ws to cat
      dispatch(addIntlError('navCategoryPanel.workspaceToCategoryError'));
    } else if (target.editable) {
      // cat to cat
      // checks if target category has editing permissions
      dispatch(moveUMO(umoList, target.id, searchParams, isCopy, origin.categoryID));
    } else {
      dispatch(addIntlError('navCategoryPanel.noEditRoleError'));
    }
  }
  dispatch(deselectAll());
};
